import React, { FC, useEffect } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getUserCycles } from '../../../services/admin/admin-user-cycles.service';
import usePaginationQueryParams from '../../../hooks/use-pagination-query-params';
import queryString from 'query-string';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvPageTitle from '../molecules/OvPageTitle';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvNoContent from '../molecules/OvNoContent';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvPaginationBar from '../molecules/OvPaginationBar';
import SyncIcon from '@mui/icons-material/Sync';
import UserCycleList from './UserCycleList';

interface OvUserCyclesProps {
  selectedUser?: UserInfo;
}

const ADMIN_USER_CYCLES_LIMIT = 10;

const OvUserCycles: FC<OvUserCyclesProps> = ({ selectedUser }) => {
  const { t } = useTranslation();

  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const query = queryString.stringify({
    limit: ADMIN_USER_CYCLES_LIMIT,
    offset,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
  });

  const {
    data: userCyclesResponse,
    isPending,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['admin-admin-user-cycles', selectedUser?.id],
    queryFn: () => getUserCycles(selectedUser?.id!, query),
    retry: 3,
    enabled: !!selectedUser?.id,
  });

  const userCycles = userCyclesResponse?.docs ?? [];
  const total = userCyclesResponse?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('start_day');
      setOrderDir('asc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const isLoading = isPending || isFetching;

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * ADMIN_USER_CYCLES_LIMIT);
  };

  return (
    <Container>
      <CenterPane>
        <HeaderItems>
          <TitleAndSearch>
            <OvPageTitle
              title={t('userDetails.tabs.userCycles')}
              icon={<SyncIcon />}
            />
          </TitleAndSearch>
          <ActionBar>
            <OvPaginationBar
              total={total}
              limit={ADMIN_USER_CYCLES_LIMIT}
              offset={offset}
              onChange={goToPage}
            />
          </ActionBar>
        </HeaderItems>

        {error && <OvErrorMessage message={error.message} />}

        {userCycles.length > 0 && <UserCycleList userCycles={userCycles} />}

        {total === 0 && !isLoading && (
          <OvNoContent>{t('common.empty')}</OvNoContent>
        )}
      </CenterPane>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Container>
  );
};

export default OvUserCycles;

const Container = styled.div`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const HeaderItems = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const TitleAndSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.5rem;
`;
